import { getBackendUrl } from '@/lib/backendUrl';
import { getApiError } from '@/lib/utils';
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { CheckCircle2, CircleDashed, AlertTriangle, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import CellarLedgerPanel from './CellarLedgerPanel';
import ExtraLinesPanel from './ExtraLinesPanel';
import { MappingBadge } from './MappingBadge';
import QboLiveInvoicePanel from './QboLiveInvoicePanel';
import { Button } from '@/components/ui/button';
import { Send } from 'lucide-react';

const API = getBackendUrl();

function authHeaders() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function formatDateOnly(isoDateStr) {
  if (!isoDateStr) return null;
  const [y, m, d] = isoDateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-GB');
}

const BLOCKING_LABELS = {
  no_site_on_job: 'No site recorded on this job.',
  no_auditors_assigned: 'No auditors are assigned to this job.',
  no_billable_lines: 'No billable service lines on this job.',
  missing_or_ambiguous_target: 'has no resolved QBO target — decide it in the Mapping Workbench.',
  not_yet_verified_in_qbo: 'is mapped but not yet verified active in QBO — check it in QBO, then verify it in Reference Lists.',
  rate_missing_or_unverified: 'Cellar Ledger monthly rate for this site is not set and verified.',
  no_months_selected: 'No Cellar Ledger months selected yet.',
  month_already_billed: 'already marked Billed at this site — remove before saving.',
  auditor_region_unassigned: 'has no QBO Region assigned yet — set it in Sales Invoicing > Settings.',
  rate_changed_since_month: 'come before this site\u2019s rate last changed — these no longer share a confirmed rate with the rest of the selection. Split them into a separate draft.',
  extra_line_target_inactive: 'extra line\u2019s service or QBO item is no longer active — remove or replace it.',
};

function BlockingList({ items }) {
  if (!items || items.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="invoice-draft-blocking-list">
      <div className="text-xs font-semibold text-amber-800 uppercase mb-2 flex items-center gap-1">
        <ShieldAlert className="h-3.5 w-3.5" />Blocking this draft — never auto-approved
      </div>
      <ul className="space-y-1">
        {items.map((b, idx) => (
          <li key={idx} className="text-sm text-amber-900 flex items-start gap-2" data-testid={`invoice-draft-blocking-${idx}`}>
            <CircleDashed className="h-3 w-3 mt-0.5 flex-shrink-0" />
            <span>{b.label ? `${b.label} ` : ''}{BLOCKING_LABELS[b.reason] || b.reason.replace(/_/g, ' ')}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function InvoiceDraftDialog({ jobId, onClose }) {
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showQboLive, setShowQboLive] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError(false);
    return axios.get(`${API}/api/sales-invoicing/jobs/${jobId}/draft`, { headers: authHeaders() })
      .then(({ data }) => setDraft(data))
      .catch((err) => {
        setLoadError(true);
        toast.error(getApiError(err, 'Could not load this invoice draft.'));
      })
      .finally(() => setLoading(false));
  }, [jobId]);

  useEffect(() => { load(); }, [load]);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="invoice-draft-dialog">
        <DialogHeader>
          <DialogTitle>Invoice draft — {draft?.hotel_name || 'Loading…'}</DialogTitle>
        </DialogHeader>

        {loading && <div className="text-sm text-slate-500" data-testid="invoice-draft-loading">Loading…</div>}
        {!loading && loadError && <div className="text-sm text-red-600" data-testid="invoice-draft-error">Could not load this invoice draft — try again.</div>}

        {!loading && !loadError && draft && (
          <div className="space-y-5" data-testid="invoice-draft-content">
            <div className="rounded-lg border border-slate-200 bg-slate-50 text-slate-600 text-xs px-3 py-2" data-testid="invoice-draft-scope-notice">
              A reviewable draft only — nothing here is exported, posted, or sent to QBO, and no invoice number is reserved.
            </div>

            <div className="flex flex-wrap gap-2 text-sm text-slate-500">
              <span>{formatDateOnly(draft.scheduled_date)}</span>
              <span>&middot;</span>
              <span>{draft.job_reference}</span>
              {draft.region ? <Badge variant="secondary" data-testid="invoice-draft-region-badge">{draft.region}</Badge> : (
                <Badge variant="outline" className="text-amber-700 border-amber-300" data-testid="invoice-draft-region-unresolved-badge">Region unresolved</Badge>
              )}
            </div>

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-1">QBO customer</div>
              <MappingBadge mapping={draft.customer} />
            </div>

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-1">Auditor roster → QBO class</div>
              <div className="text-sm text-slate-700 mb-1">{(draft.auditor_names || []).join(', ') || '— none assigned —'}</div>
              <MappingBadge mapping={draft.auditor_mapping} />
            </div>

            <Separator />

            <div>
              <div className="text-xs font-medium text-slate-400 uppercase mb-2">Service-line recipe</div>
              <div className="space-y-2" data-testid="invoice-draft-service-lines">
                {draft.service_lines.length === 0 && <div className="text-sm text-slate-500">No billable lines on this job.</div>}
                {draft.service_lines.filter((s) => !s.is_cellar_ledger).map((s) => (
                  <div key={s.pricing_line_id} className="flex items-center justify-between gap-3 text-sm border rounded-md p-2" data-testid={`invoice-draft-service-line-${s.pricing_line_id}`}>
                    <div>
                      <div className="font-medium">{s.service_name}</div>
                      <div className="text-slate-400 text-xs">Contract rate &middot; £{s.price.toFixed(2)}</div>
                    </div>
                    <MappingBadge mapping={s.mapping} />
                  </div>
                ))}
              </div>
            </div>

            {draft.has_cellar_ledger && (
              <CellarLedgerPanel jobId={jobId} draft={draft} onChanged={load} />
            )}

            <ExtraLinesPanel jobId={jobId} draft={draft} onChanged={load} />

            <Separator />

            <div className="space-y-1 text-sm" data-testid="invoice-draft-totals">
              <div className="flex justify-between"><span className="text-slate-500">Net</span><span>£{draft.net.toFixed(2)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">VAT ({(draft.vat_rate * 100).toFixed(0)}%)</span><span>£{draft.vat.toFixed(2)}</span></div>
              <div className="flex justify-between font-semibold text-base pt-1 border-t"><span>Total inc. VAT</span><span data-testid="invoice-draft-total">£{draft.total_inc_vat.toFixed(2)}</span></div>
              {draft.financials_indicative_only && (
                <div className="text-xs text-amber-700 flex items-center gap-1 pt-1" data-testid="invoice-draft-indicative-notice">
                  <AlertTriangle className="h-3.5 w-3.5" />Indicative only — blocked below until every decision is resolved.
                </div>
              )}
            </div>

            {draft.is_ready ? (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-800 text-sm px-3 py-2 flex items-center gap-2" data-testid="invoice-draft-ready-notice">
                <CheckCircle2 className="h-4 w-4" />Ready for review — every target is resolved locally. Still not exported or posted; a separate, stricter check runs again before any direct QBO create.
              </div>
            ) : (
              <BlockingList items={draft.blocking_decisions} />
            )}

            <Button variant="outline" className="w-full" onClick={() => setShowQboLive(true)} data-testid="invoice-draft-open-qbo-live-btn">
              <Send className="h-4 w-4 mr-1.5" />Create in QBO (Sandbox)
            </Button>
          </div>
        )}
      </DialogContent>

      {showQboLive && <QboLiveInvoicePanel jobId={jobId} onClose={() => setShowQboLive(false)} />}
    </Dialog>
  );
}
